import { Component, Vue, Watch } from "vue-property-decorator";
import WithRender from "./Requests.html";
import { Inject } from "vue-di-container";
import UserService from "@/shared/userService/UserService";
import ProviderService from "@/components/Provider/ProviderService";
import { DropdownModel } from "@/components/Provider/ProviderModel";
import { Toaster } from "@/Services/toast";
import { RequestsFilterModel } from "./RequestsFilterModel";
import RequestsOverviewPage from "./Overview/RequestsOverviewPage";
import RequestsService from "./RequestsService";
import RequestsSendersPage from "./Senders/RequestsSendersPage";
import { DatePicker } from "v-calendar";

@WithRender
@Component({
    components: {
        RequestsOverviewPage,
        RequestsSendersPage,
        VcDatePicker: DatePicker,
    },
    diProvide: [UserService, ProviderService, RequestsService],
})
export default class Requests extends Vue {
    @Inject(UserService) public userService!: UserService;
    @Inject(RequestsService) public requestsService!: RequestsService;
    @Inject(ProviderService) public providerService!: ProviderService;

    public isRequestsReady: boolean = false;
    public requestsTab: number = 0;

    public readonly launchDate: string = "2022-02-01";
    private readonly emptyGuid: string = "00000000-0000-0000-0000-000000000000";

    public readonly monitorDashboardSelectMenuProps: Record<string, unknown> = {
        contentClass: "monitor-dashboard-select-menu",
        offsetY: false,
        maxHeight: 260,
    };

    public tenants: DropdownModel[] = [];
    public providers: DropdownModel[] = [];

    public get requestTypes(): DropdownModel[] {
        return [
            { id: 0, name: String(this.$t("monitorDashboard.all")) },
            { id: 1, name: String(this.$t("monitorDashboard.senderRequest")) },
            { id: 2, name: String(this.$t("monitorDashboard.contractRequest")) },
        ];
    }

    public get customerTypes(): DropdownModel[] {
        return [
            { id: 0, name: String(this.$t("monitorDashboard.all")) },
            { id: 1, name: String(this.$t("monitorDashboard.government")) },
            { id: 3, name: String(this.$t("monitorDashboard.private")) },
            { id: 2, name: String(this.$t("monitorDashboard.charity")) },
        ];
    }

    public createdDateRangeInternal: any = {
        start: "",
        end: "",
    };

    public createdDateModelConfig: any = {
        type: "string",
        mask: "YYYY-MM-DD",
    };
    private reloadTimer: any = null;
    private reloadIntervalMs: number = 15 * 60 * 1000;
    private isAutoRefreshing: boolean = false;

    public overviewChartFilter: RequestsFilterModel = {
        dateFrom: "2022-02-01",
        dateTo: "",
        tenant: "00000000-0000-0000-0000-000000000000",
        providerId: "00000000-0000-0000-0000-000000000000",
        customerType: 0,
        requestType: 0,
    };

    public sendersChartFilter: RequestsFilterModel = {
        dateFrom: "2022-02-01",
        dateTo: "",
        tenant: "00000000-0000-0000-0000-000000000000",
        providerId: "00000000-0000-0000-0000-000000000000",
        customerType: 0,
        requestType: 0,
    };

    private overviewDraftFilter: RequestsFilterModel = {
        dateFrom: "2022-02-01",
        dateTo: "",
        tenant: "00000000-0000-0000-0000-000000000000",
        providerId: "00000000-0000-0000-0000-000000000000",
        customerType: 0,
        requestType: 0,
    };

    private sendersDraftFilter: RequestsFilterModel = {
        dateFrom: "2022-02-01",
        dateTo: "",
        tenant: "00000000-0000-0000-0000-000000000000",
        providerId: "00000000-0000-0000-0000-000000000000",
        customerType: 0,
        requestType: 0,
    };

    public draftChartFilter: RequestsFilterModel = {
        dateFrom: "2022-02-01",
        dateTo: "",
        tenant: "00000000-0000-0000-0000-000000000000",
        providerId: "00000000-0000-0000-0000-000000000000",
        customerType: 0,
        requestType: 0,
    };

    public showNoDataToastAfterApply: boolean = false;

    public get isDraftDateRangeInvalid(): boolean {
        const from =
            this.createdDateRangeInternal && this.createdDateRangeInternal.start
                ? this.createdDateRangeInternal.start
                : this.draftChartFilter.dateFrom;
        const to =
            this.createdDateRangeInternal && this.createdDateRangeInternal.end
                ? this.createdDateRangeInternal.end
                : this.draftChartFilter.dateTo;
        if (!from || !to) {
            return false;
        }
        return String(from) > String(to);
    }

    public get todayStr(): string {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }

    public get dateRangeText(): string {
        const from =
            this.createdDateRangeInternal &&
                this.createdDateRangeInternal.start
                ? this.createdDateRangeInternal.start
                : this.draftChartFilter && this.draftChartFilter.dateFrom
                    ? this.draftChartFilter.dateFrom
                    : this.launchDate;

        const to =
            this.createdDateRangeInternal &&
                this.createdDateRangeInternal.end
                ? this.createdDateRangeInternal.end
                : this.draftChartFilter && this.draftChartFilter.dateTo
                    ? this.draftChartFilter.dateTo
                    : this.todayStr;

        return from + " - " + to;
    }

    private syncDraftDatesFromPicker(): void {
        const from =
            this.createdDateRangeInternal && this.createdDateRangeInternal.start
                ? this.createdDateRangeInternal.start
                : "";
        const to =
            this.createdDateRangeInternal && this.createdDateRangeInternal.end
                ? this.createdDateRangeInternal.end
                : "";

        if (from && !to) {
            this.draftChartFilter.dateFrom = from;
            this.draftChartFilter.dateTo = from;
            return;
        }

        if (from && to) {
            this.draftChartFilter.dateFrom = from;
            this.draftChartFilter.dateTo = to;
            return;
        }
    }

    private syncPickerFromDraft(): void {
        const from = this.draftChartFilter.dateFrom || this.launchDate;
        const to = this.draftChartFilter.dateTo || this.todayStr;

        this.createdDateRangeInternal = {
            start: from,
            end: to,
        };
    }

    private buildDefaultFilter(dateFrom: string, dateTo: string): RequestsFilterModel {
        return {
            dateFrom,
            dateTo,
            tenant: this.emptyGuid,
            providerId: this.emptyGuid,
            customerType: 0,
            requestType: 0,
        };
    }

    public async reload(): Promise<void> {
        this.isRequestsReady = false;

        const initialCalls = await Promise.all([
            this.requestsService.GetTenants(),
            this.providerService.GetProviderName(),
            this.requestsService.GetDefaultDates(),
            this.requestsService.GetMonitorDashboardOptions(),
        ]);

        const tenantsResult = initialCalls[0];
        const safeTenants = Array.isArray(tenantsResult) ? tenantsResult : [];

        this.tenants = [{ id: this.emptyGuid, name: "All" }].concat(
            safeTenants.map((t: any) => ({ id: t.id, name: t.name })),
        );

        const response = initialCalls[1];
        const providerResult =
            response && (response as any).result ? (response as any).result : [];
        this.providers = [{ id: this.emptyGuid, name: "All" }].concat(
            providerResult.map((p: any) => ({ id: p.id, name: p.name })),
        );

        const def = initialCalls[2];
        const dateFrom = def && def.dateFrom ? def.dateFrom : this.launchDate;
        const dateTo = def && def.dateTo ? def.dateTo : this.todayStr;

        const fresh = this.buildDefaultFilter(dateFrom, dateTo);

        this.overviewChartFilter = { ...fresh };
        this.sendersChartFilter = { ...fresh, requestType: 0 };

        this.overviewDraftFilter = { ...fresh };
        this.sendersDraftFilter = { ...fresh, requestType: 0 };

        this.draftChartFilter = { ...this.overviewDraftFilter };
        this.syncPickerFromDraft();

        const opts = initialCalls[3];
        const reloadInterval =
            opts && opts.reloadInterval ? Number(opts.reloadInterval) : 15;
        this.reloadIntervalMs = reloadInterval * 60 * 1000;
        this.startReloadTimer();

        this.isRequestsReady = true;

        await this.$nextTick();
        await this.fetchChartsForCurrentFilter();
    }

    public beforeDestroy(): void {
        this.clearReloadTimer();
    }

    public async applyFilters(): Promise<void> {
        this.syncDraftDatesFromPicker();
        if (this.isDraftDateRangeInvalid) {
            Toaster.error(String(this.$t("monitorDashboard.invalidDateRange")));
            return;
        }

        if (this.requestsTab === 0) {
            this.overviewDraftFilter = { ...this.draftChartFilter };
            this.overviewChartFilter = { ...this.draftChartFilter };
        } else {
            this.draftChartFilter.requestType = 0;
            this.sendersDraftFilter = { ...this.draftChartFilter, requestType: 0 };
            this.sendersChartFilter = { ...this.draftChartFilter, requestType: 0 };
        }

        this.showNoDataToastAfterApply = true;

        await this.$nextTick();
        await this.fetchChartsForCurrentFilter();

        this.showNoDataToastAfterApply = false;
    }

    public async resetFilters(): Promise<void> {
        const dateFrom = this.launchDate;
        const dateTo = this.todayStr;

        const def = this.buildDefaultFilter(dateFrom, dateTo);

        this.draftChartFilter =
            this.requestsTab === 0 ? { ...def } : { ...def, requestType: 0 };

        this.syncPickerFromDraft();

        await this.applyFilters();
    }

    @Watch("requestsTab")
    public async onRequestsTabChanged(): Promise<void> {
        if (this.requestsTab === 0) {
            this.draftChartFilter = { ...this.overviewDraftFilter };
        } else {
            this.draftChartFilter = { ...this.sendersDraftFilter, requestType: 0 };
        }

        this.syncPickerFromDraft();

        if (!this.isRequestsReady) return;

        this.startReloadTimer();

        await this.$nextTick();
        await this.fetchChartsForCurrentFilter();
    }

    public async refresh(): Promise<void> {
        await this.fetchChartsForCurrentFilter();
    }

    private async fetchChartsForCurrentFilter(): Promise<void> {
        if (!this.isRequestsReady) return;

        if (this.requestsTab === 0) {
            const cmp: any = this.$refs.overviewPage;
            if (cmp && cmp.refresh) {
                await cmp.refresh();
            }
            return;
        }

        const cmp: any = this.$refs.sendersPage;
        if (cmp && cmp.refresh) {
            await cmp.refresh();
        }
    }

    public async mounted(): Promise<void> {
        const q = this.$route.query;
        if (q && q.requestsTab != null && String(q.requestsTab) !== "") {
            const tab = parseInt(String(q.requestsTab), 10);
            if (!isNaN(tab)) {
                this.requestsTab = tab;
            }
        }
        await this.reload();
    }

    public created(): void {
        const today = this.todayStr;
        this.overviewChartFilter.dateTo = today;
        this.sendersChartFilter.dateTo = today;
        this.draftChartFilter.dateTo = today;
        this.createdDateRangeInternal = {
            start: this.draftChartFilter.dateFrom,
            end: this.draftChartFilter.dateTo,
        };
    }

    private clearReloadTimer(): void {
        if (this.reloadTimer) {
            clearInterval(this.reloadTimer);
            this.reloadTimer = null;
        }
    }

    private startReloadTimer(): void {
        const self = this;
        this.clearReloadTimer();
        if (!this.reloadIntervalMs || this.reloadIntervalMs <= 0) return;

        this.reloadTimer = setInterval(async function () {
            if (!self.isRequestsReady) return;
            if (self.isAutoRefreshing) return;

            self.isAutoRefreshing = true;
            try {
                await self.fetchChartsForCurrentFilter();
            } finally {
                self.isAutoRefreshing = false;
            }
        }, this.reloadIntervalMs);
    }
}
